import { useEffect, useMemo, useState } from 'react';
import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from '@chakra-ui/react';
import { ReactSVG } from 'react-svg';
import rightArrow from '../../../assets/images/icons/right-arrow.svg';
import { Drawer } from '../../../components/UI-CHAKRA/Drawer';
import { DrawerOverlay } from '../../../components/UI-CHAKRA/DrawerOverlay';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { Checkbox } from '../../../components/UI-CHAKRA/Checkbox';
import { Spinner } from '../../../components/UI-CHAKRA/Spinner';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { useLoading } from '../../../helpers/useLoading';
import { logger } from '../../../helpers/logger';
import { Model, ModelDetails, ModelService } from '../services';
import { FormulaService } from '../../../components/search/formulaService';
import { ModelEntryPoint, useModelStore } from '../../../stores/modelStore';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { getMedicalCertificateLabel } from './medicalCertificateLabel';

interface ApplyModelDrawerProps {
  isOpen: boolean;
  model: Model | null;
  onClose: () => void;
  /**
   * Chamado depois que o modelo entrou na prescrição, antes do fechamento.
   *
   * Separado do `onClose` porque este avisa só que o drawer saiu de cena — cancelar também o
   * dispara. Quem precisa reagir à aplicação em si, como a listagem lateral que se fecha junto,
   * não pode se apoiar nele: desistir do modelo deve devolver o prescritor à lista.
   */
  onApplied?: () => void;
  /** De onde o modelo foi aberto (tela principal ou menu lateral), para o tracking da aplicação. */
  entryPoint: ModelEntryPoint;
}

type SelectionType =
  | 'medication'
  | 'exam'
  | 'certificate'
  | 'referral'
  | 'report'
  | 'compoundedFormula';

type SelectionState = Record<SelectionType, Set<number>>;

const EMPTY_SELECTION: SelectionState = {
  medication: new Set<number>(),
  exam: new Set<number>(),
  certificate: new Set<number>(),
  referral: new Set<number>(),
  report: new Set<number>(),
  compoundedFormula: new Set<number>(),
};

const CATEGORY_LABEL: Record<string, string> = {
  sugestao: 'Sugestão',
  institucional: 'Institucional',
  meusModelos: 'Meus modelos',
};

function getCategoryLabel(model: Model): string {
  if (model.sponsored) return CATEGORY_LABEL.sugestao;
  if (model.origin === 'institutional') return CATEGORY_LABEL.institucional;
  return CATEGORY_LABEL.meusModelos;
}

export function ApplyModelDrawer(props: ApplyModelDrawerProps) {
  const { isOpen, model, onClose, onApplied, entryPoint } = props;

  const [details, setDetails] = useState<ModelDetails | null>(null);
  const [formulaNames, setFormulaNames] = useState<Record<number, string>>({});
  const [loadingFormulaNames, setLoadingFormulaNames] = useState(false);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [loadingDetails, runWithLoadingDetails] = useLoading();
  const [applying, runWithApplying] = useLoading();
  const isMobile = useIsMobile();

  const prescription = usePrescriptionStore((store) => store.prescription);
  const prescriber = usePrescriptionStore((store) => store.prescriber());
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const applyModelToPrescription = useModelStore((store) => store.applyModelToPrescription);

  useEffect(() => {
    if (!isOpen || !model || !prescription) return;
    let cancelled = false;
    runWithLoadingDetails(async () => {
      try {
        const data = await ModelService.fetchModelDetails(
          model.id,
          prescription.Prescritor?.idPrescritor ?? 0,
          prescriberConfiguration?.idParceiro ?? 0,
          prescription.idPrescricao,
        );
        if (!cancelled) setDetails(data);
      } catch (e) {
        logger.error('Error fetching model details:', e);
        if (!cancelled) setDetails(null);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, model?.id, prescription?.idPrescricao, prescriberConfiguration?.idParceiro]);

  const flatExamItems = useMemo(
    () => (details?.exams ?? []).flatMap((group) => group.items ?? []),
    [details]
  );

  useEffect(() => {
    if (!isOpen) {
      setDetails(null);
      setSelection(EMPTY_SELECTION);
      return;
    }
    if (!details) return;
    setSelection({
      medication: new Set((details.medications ?? []).map((_, idx) => idx)),
      exam: new Set(flatExamItems.map((_, idx) => idx)),
      certificate: new Set((details.certificates ?? []).map((_, idx) => idx)),
      referral: new Set((details.referrals ?? []).map((_, idx) => idx)),
      report: new Set((details.reports ?? []).map((_, idx) => idx)),
      compoundedFormula: new Set((details.compoundedFormulas ?? []).map((_, idx) => idx)),
    });
  }, [isOpen, details, flatExamItems]);

  useEffect(() => {
    if (!isOpen || !details?.compoundedFormulas?.length) {
      setFormulaNames({});
      setLoadingFormulaNames(false);
      return;
    }
    let cancelled = false;
    setLoadingFormulaNames(true);
    (async () => {
      const entries = await Promise.all(
        details.compoundedFormulas.map(async (formula) => {
          try {
            const lib = await FormulaService.getDoctorFormulaById(details.doctorId, formula.formulaId);
            return [formula.formulaId, lib.formulaName ?? ''] as const;
          } catch {
            return [formula.formulaId, ''] as const;
          }
        })
      );
      if (!cancelled) {
        setFormulaNames(Object.fromEntries(entries.filter(([, name]) => name)));
        setLoadingFormulaNames(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, details]);

  const totalCounts = useMemo(() => ({
    medication: details?.medications?.length ?? 0,
    exam: flatExamItems.length,
    certificate: details?.certificates?.length ?? 0,
    referral: details?.referrals?.length ?? 0,
    report: details?.reports?.length ?? 0,
    compoundedFormula: details?.compoundedFormulas?.length ?? 0,
  }), [details, flatExamItems]);

  const hasAnyItem =
    totalCounts.medication > 0 ||
    totalCounts.exam > 0 ||
    totalCounts.certificate > 0 ||
    totalCounts.referral > 0 ||
    totalCounts.report > 0 ||
    totalCounts.compoundedFormula > 0;

  const anyItemSelected = useMemo(
    () =>
      selection.medication.size > 0 ||
      selection.exam.size > 0 ||
      selection.certificate.size > 0 ||
      selection.referral.size > 0 ||
      selection.report.size > 0 ||
      selection.compoundedFormula.size > 0,
    [selection]
  );

  const allItemsSelected = useMemo(
    () =>
      selection.medication.size === totalCounts.medication &&
      selection.exam.size === totalCounts.exam &&
      selection.certificate.size === totalCounts.certificate &&
      selection.referral.size === totalCounts.referral &&
      selection.report.size === totalCounts.report &&
      selection.compoundedFormula.size === totalCounts.compoundedFormula,
    [selection, totalCounts]
  );

  const toggle = (type: SelectionType, idx: number) => {
    setSelection((prev) => {
      const set = new Set(prev[type]);
      if (set.has(idx)) set.delete(idx);
      else set.add(idx);
      return { ...prev, [type]: set };
    });
  };

  const handleApply = () => {
    if (!model) return;
    runWithApplying(async () => {
      await applyModelToPrescription(model, allItemsSelected ? undefined : selection, details ?? undefined, entryPoint);
      onApplied?.();
      onClose();
    });
  };

  const handleClose = () => {
    if (applying) return;
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement={'right'} onClose={handleClose} size={'md'}>
      <DrawerOverlay bg={'rgba(0, 0, 0, 0.4)'} />
      <DrawerContent maxW={{ base: '100%', md: '445px' }} data-cy={'aplicar-modelo-drawer'}>
        <DrawerHeader
          display={'flex'}
          flexDirection={'column'}
          alignItems={'flex-start'}
          gap={'4px'}
          h={'72px'}
          px={'24px'}
          py={'16px'}
          borderBottom={'1px solid #DEE2E6'}
        >
          <Text fontSize={'18px'} fontWeight={600} color={'#1E252B'} lineHeight={'1.2'}>
            {model?.name ?? ''}
          </Text>
          {model && (
            <Text fontSize={'12px'} color={'#868E96'} lineHeight={'1.2'}>
              {getCategoryLabel(model)}
            </Text>
          )}
        </DrawerHeader>
        <DrawerCloseButton top={'24px'} right={'24px'} color={'#1E252B'} />

        <DrawerBody p={0} overflowY={'auto'}>
          {loadingDetails && (
            <Flex justifyContent={'center'} py={'32px'}>
              <Spinner thickness={'2px'} color={'primary.main'} />
            </Flex>
          )}

          {!loadingDetails && !hasAnyItem && (
            <Box px={'24px'} py={'24px'}>
              <Text fontSize={'14px'} color={'#868E96'}>
                Este modelo não possui itens.
              </Text>
            </Box>
          )}

          {!loadingDetails && totalCounts.medication > 0 && (
            <Section title={'Medicamento'}>
              {(details?.medications ?? []).map((item, idx) => (
                <ItemRow
                  key={`med-${item.id}`}
                  primary={item.brandName ?? item.activeIngredient ?? ''}
                  secondary={item.posology ?? ''}
                  isChecked={selection.medication.has(idx)}
                  onToggle={() => toggle('medication', idx)}
                />
              ))}
            </Section>
          )}

          {!loadingDetails && totalCounts.compoundedFormula > 0 && (
            <Section title={'Fórmula'}>
              {(details?.compoundedFormulas ?? []).map((item, idx) => (
                <ItemRow
                  key={`formula-${item.id}`}
                  primary={
                    loadingFormulaNames
                      ? <Spinner size={'xs'} thickness={'2px'} color={'primary.main'} />
                      : (formulaNames[item.formulaId] ?? 'Fórmula')
                  }
                  isChecked={selection.compoundedFormula.has(idx)}
                  onToggle={() => toggle('compoundedFormula', idx)}
                />
              ))}
            </Section>
          )}

          {!loadingDetails && totalCounts.exam > 0 && (
            <Section title={'Exames e Cirurgias'}>
              {flatExamItems.map((item, idx) => (
                <ItemRow
                  key={`exam-${item.id}`}
                  primary={item.description ?? ''}
                  secondary={item.observation ?? ''}
                  isChecked={selection.exam.has(idx)}
                  onToggle={() => toggle('exam', idx)}
                />
              ))}
            </Section>
          )}

          {!loadingDetails && totalCounts.certificate > 0 && (
            <Section title={'Atestado'}>
              {(details?.certificates ?? []).map((item, idx) => (
                <ItemRow
                  key={`cert-${item.id}`}
                  primary={getMedicalCertificateLabel(item.type, prescriber?.Conselho)}
                  secondary={item.reason ?? item.observation ?? ''}
                  isChecked={selection.certificate.has(idx)}
                  onToggle={() => toggle('certificate', idx)}
                />
              ))}
            </Section>
          )}

          {!loadingDetails && totalCounts.referral > 0 && (
            <Section title={'Encaminhamento'}>
              {(details?.referrals ?? []).map((item, idx) => (
                <ItemRow
                  key={`ref-${item.id}`}
                  primary={item.specialty ?? 'Encaminhamento'}
                  secondary={item.reason ?? ''}
                  isChecked={selection.referral.has(idx)}
                  onToggle={() => toggle('referral', idx)}
                />
              ))}
            </Section>
          )}

          {!loadingDetails && totalCounts.report > 0 && (
            <Section title={'Relatório'}>
              {(details?.reports ?? []).map((item, idx) => (
                <ItemRow
                  key={`report-${item.id}`}
                  primary={item.documentType || 'Relatório'}
                  secondary={item.description ?? ''}
                  isChecked={selection.report.has(idx)}
                  onToggle={() => toggle('report', idx)}
                />
              ))}
            </Section>
          )}
        </DrawerBody>

        <DrawerFooter
          borderTop={'1px solid #DEE2E6'}
          gap={{ base: '12px', md: '16px' }}
          justifyContent={{ base: 'space-between', md: 'flex-end' }}
          py={'20px'}
        >
          <Button
            variant={'outline'}
            borderRadius={'20px'}
            borderColor={'primary.main'}
            color={'primary.main'}
            minW={'80px'}
            flex={{ base: 1, md: 'unset' }}
            h={'40px'}
            onClick={handleClose}
            isDisabled={applying}
          >
            Voltar
          </Button>
          <Button
            variant={'solid'}
            borderRadius={'20px'}
            minW={'130px'}
            flex={{ base: 1, md: 'unset' }}
            h={'40px'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={'8px'}
            onClick={handleApply}
            isLoading={applying}
            isDisabled={!hasAnyItem || !anyItemSelected || loadingDetails}
            rightIcon={isMobile ? <ReactSVG src={rightArrow} /> : undefined}
            data-testid={'aplicar-modelo-submit'}
          >
            Adicionar
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Box px={'24px'} py={'8px'}>
        <Text fontSize={'14px'} fontWeight={600} color={'#1E252B'}>
          {props.title}
        </Text>
      </Box>
      {props.children}
    </Box>
  );
}

interface ItemRowProps {
  primary: React.ReactNode;
  secondary?: string;
  isChecked: boolean;
  multiline?: boolean;
  isReadOnly?: boolean;
  onToggle?: () => void;
}

function ItemRow(props: ItemRowProps) {
  const { primary, secondary, isChecked, multiline, isReadOnly, onToggle } = props;
  const primaryColor = usePrescriptionStore((store) => store.primaryColor());
  const checkboxSx = {
    '.chakra-checkbox__control[data-checked]': {
      background: primaryColor,
      borderColor: primaryColor,
      color: 'white',
    },
    '.chakra-checkbox__control[data-checked]:hover': {
      background: primaryColor,
      borderColor: primaryColor,
    },
  };

  return (
    <Flex
      alignItems={'flex-start'}
      gap={'12px'}
      px={'24px'}
      py={'12px'}
      borderTop={'1px solid #F1F3F5'}
    >
      <Box pt={'2px'}>
        <Checkbox
          isChecked={isChecked}
          isReadOnly={isReadOnly}
          onChange={isReadOnly ? undefined : onToggle}
          sx={checkboxSx}
        />
      </Box>
      <Flex direction={'column'} gap={'4px'} minW={0} flex={1}>
        <Text
          fontSize={'14px'}
          fontWeight={500}
          color={'#1E252B'}
          lineHeight={'1.3'}
          whiteSpace={multiline ? 'pre-wrap' : 'normal'}
        >
          {primary}
        </Text>
        {secondary && (
          <Text fontSize={'12px'} color={'#868E96'} lineHeight={'1.3'}>
            {secondary}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}
